import { useLayoutEffect, useRef } from 'preact/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import classNames from 'classnames';
import Address, { AddressProps } from './address/address';
import { isNil as _isNil } from 'lodash';
import { FunctionComponent, h } from 'preact';
import { useAriaFocusRef } from '../../../helpers/aria-helper';
import { useTranslation } from '../../../hooks/use-translation';

interface SelectPickup {
	addresses: AddressProps[];
	onClickAddress: (index: number) => void;
	activeIndex: number;
	onCancel: () => void;
	onSubmit: () => void;
	translations: Translations;
}

interface Translations {
	header: string;
	submitButtonText: string;
}

// TODO: take the consts from scss when we start using scss modules
const TRANSITION_TIME = 500;
const ROW_HEIGHT = 60;

const scrollRows = (rows: HTMLDivElement, bottomDrawer: HTMLDivElement) => {
	let start = null;
	const initialScrollTop = rows.scrollTop;

	const step = timestamp => {
		if (!start) start = timestamp;
		const progress = timestamp - start;
		rows.scrollTop = initialScrollTop + bottomDrawer.offsetHeight;

		if (progress < TRANSITION_TIME) {
			window.requestAnimationFrame(step);
		}
	};

	window.requestAnimationFrame(step);
};

const SelectPickup: FunctionComponent<SelectPickup> = ({
	addresses,
	onClickAddress,
	activeIndex,
	onCancel,
	onSubmit,
	translations
}) => {
	const rowsRef = useRef<HTMLDivElement>(null);
	const headerRef = useAriaFocusRef<HTMLDivElement>(null);
	const bottomDrawerRef = useRef<HTMLDivElement>(null);
	const prevActiveIndex = useRef(activeIndex);
	const onClickAddressWithIndex = index => {
		return () => onClickAddress(index);
	};
	const { t } = useTranslation();

	useLayoutEffect(() => {
		const isBottomDrawerOpening = _isNil(prevActiveIndex) && !_isNil(activeIndex);
		if (isBottomDrawerOpening) {
			if (bottomDrawerRef.current && rowsRef.current) {
				const isVisible =
					activeIndex * ROW_HEIGHT <=
					rowsRef.current.scrollTop + rowsRef.current.offsetHeight - ROW_HEIGHT * 2;
				if (!isVisible) {
					scrollRows(rowsRef.current, bottomDrawerRef.current);
				}
			}
		}
	}, [prevActiveIndex, activeIndex]);

	return (
		<div className="select-pickup">
			<div className="select-pickup-header select-pickup-row" ref={headerRef}>
				<span className="aria-focus" tabIndex={0}>
					{translations.header}
				</span>
				<div className="select-pickup-cancel" onClick={onCancel}>
					<FontAwesomeIcon aria-hidden="false" role="button" aria-label={t('GLOBAL.CANCEL')} icon={faTimes} />
				</div>
			</div>
			<div className="select-pickup-rows" ref={rowsRef} role="listbox">
				{addresses.map((addressDetails, index) => {
					const selected = activeIndex === index;

					return (
						<div
							key={index}
							className={classNames('select-pickup-row', { selected })}
							onClick={onClickAddressWithIndex(index)}
							role="option"
							aria-selected={selected}
						>
							<Address {...addressDetails} position={index + 1} />
						</div>
					);
				})}
			</div>
			<div className={`bottom-drawer ${classNames({ open: !_isNil(activeIndex) })}`} ref={bottomDrawerRef}>
				{/* TODO: use custom button when we create one  */}
				<div className="select-pickup-submit-button" onClick={onSubmit} role="button">
					{translations.submitButtonText}
				</div>
			</div>
		</div>
	);
};

export default SelectPickup;
